<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref, useId, watch } from 'vue'
import { useRoute } from 'vue-router'
import bLabel from '../common/bLabel.vue'
import PhWarningDuotone from '~icons/ph/warning-duotone'
import IcRoundKeyboardArrowDown from '~icons/ic/round-keyboard-arrow-down'
import { RouterLink } from 'vue-router'

const props = defineProps<{
  menuItem: {
    isHeader: boolean
    prependIcon: any
    display: string
    route: string
    isAlpha: boolean
    isBeta: boolean
    params: any
    moduleId: number | string
    link?: string
    /** Count shown after the label (and as a dot on the icon while the drawer is collapsed). */
    badge?: number
    subPages: {
      isHeader: boolean
      prependIcon: any
      display: string
      route: string
      isAlpha: boolean
      isBeta: boolean
      params: any
      moduleId: number | string
    }[]
  }
}>()
const emit = defineEmits<{
  (event: 'click', ...args: any[]): void
}>()

const route = useRoute()

const isCurrentModule = computed(() => route.meta.moduleName === props.menuItem.moduleName)

// Open on the current module, closed elsewhere; the header toggles it either way (its chevron says which).
const isSubMenuOpen = ref(isCurrentModule.value)
watch(() => route.path, () => {
  isSubMenuOpen.value = isCurrentModule.value
})
const hasSubPages = computed(() => !!props.menuItem.subPages?.length)
const subMenuId = useId()
// Colour of the gliding highlight (DrawerMenuList), taken from the item's `color` classes (`hover:border-red-800!` → `red-800`).
const tone = computed(() => props.menuItem.color?.match(/border-([a-z]+-\d+)/)?.[1] ?? 'primary-500')
const clickOnMenu = (isHeader = false) => {
  if (isHeader)
    isSubMenuOpen.value = !isSubMenuOpen.value
  else emit('click', false)
}

const { t, locale } = useI18n()

const addProperties = computed(() => {
  const toSend = {}
  if(props.menuItem.link) {
    toSend['href'] = props.menuItem.link
    toSend['target'] = '_blank'
  }else {
    toSend['to'] = { name: props.menuItem.route, params: props.menuItem.params }
  }
  return toSend
})


</script>

<template>
  <li class="pr-2 select-none">
    <component :is="menuItem.link ? 'a' : RouterLink" 
      v-if="!menuItem.isHeader" v-bind="addProperties"
      class="menu-item cursor-pointer" :data-tone="tone" @click="clickOnMenu(true)" :data-cy="`special-items-${menuItem.moduleId}`"
    >
      <span class="relative inline-flex justify-center items-center w-12 shrink-0">
        <component :is="menuItem.prependIcon" />
        <span v-if="menuItem.badge" class="menu-item-badge-dot absolute right-2.5 -top-1 hidden h-2 w-2 rounded-full bg-primary-500 md:block" aria-hidden="true" />
      </span>
      <span class="ml-1 text-sm tracking-wide truncate">{{ t(menuItem.display) }}</span>
      <span
        v-if="menuItem.badge"
        class="ml-auto min-w-5 shrink-0 rounded-full bg-primary-500 px-1.5 text-center text-xs font-semibold leading-5 text-white tabular-nums"
        :aria-label="t('common.drawer.badgeCount', { count: menuItem.badge })"
      >{{ menuItem.badge > 99 ? '99+' : menuItem.badge }}</span>
       <!-- {{ menuItem.display }} -->
       <!-- {{ t(menuItem.display) }} -->
         
         <b-label v-if="menuItem.isBeta" v-tooltip="'BETA, some bugs could still be present and minor features missing'" bg-color="warning-op" class="ml-auto" size="dense">
            <PhWarningDuotone />
          </b-label>
    </component>
    <button
      v-else type="button" :data-tone="tone" class="menu-header relative flex flex-row items-center w-full h-8 cursor-pointer text-left focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-500"
      :aria-expanded="hasSubPages ? isSubMenuOpen : undefined" :aria-controls="hasSubPages ? subMenuId : undefined"
      :data-cy="`header-${menuItem.moduleName}`" @click="clickOnMenu(true)"
    >
      <div class="tracking-wide flex items-center uppercase whitespace-nowrap hover:text-primary-400" :class="[{ 'font-semibold text-primary-500': isCurrentModule }]">
        <span v-if="menuItem.prependIcon" class="inline-flex justify-center items-center w-14 shrink-0">
          <component :is="menuItem.prependIcon" />
        </span>
        <p class="menu-item-show-hover md:opacity-0">
          {{ t(menuItem.display) }}
        </p>
      </div>
      <span v-if="hasSubPages" class="menu-item-show-hover md:opacity-0 ml-auto mr-2 text-muted" aria-hidden="true">
        <IcRoundKeyboardArrowDown class="transition-transform duration-200 motion-reduce:transition-none" :class="{ 'rotate-180': isSubMenuOpen }" />
      </span>
    </button>
    <transition name="fade" duration="200">
      <ul v-if="hasSubPages && isSubMenuOpen" :id="subMenuId" class="pt-0.5 pb-1">
        <li
          v-for="(page) in menuItem.subPages" :key="`${page.route}-${page.params?.()}`"
          class="sub-menu-item border-l-2 relative last:border-0! last:after:left-0 border-gray-500 after:transform after:top-0 after:bg-transparent after:w-4 after:h-4 after:border-l-2 after:border-b-2 after:rounded-bl-xl after:absolute after:left-[-2px] after:border-gray-500 after:border-0"
          @click="clickOnMenu()"
        >
          <router-link
            v-if="!page.isHeader" :data-cy="`${page.route}-${page.params?.() || ''}`" :to="{ name: page.route, params: page.params?.() || {} }"
            class="menu-item gap-2" :data-tone="tone" :class="{ 'text-primary-500!': route.name === page.route }" @click="emit('click', false)"
          >
            <span class="sub-menu-item-icon inline-flex justify-center items-center w-6 shrink-0">
              <component :is="page.prependIcon" />
            </span>
            <span class="text-sm tracking-wide truncate">{{ t(page.display) }} </span>
            <b-label v-if="page.isAlpha" v-tooltip="'ALPHA, Bugs are likely to be present and missing features are expected'" bg-color="error-op" class="ml-auto" size="dense">
              <PhWarningDuotone />
            </b-label>
            <b-label v-if="page.isBeta" v-tooltip="'BETA, some bug could still be present and not critical features missing'" bg-color="warning-op" class="ml-auto" size="dense">
              <PhWarningDuotone />
            </b-label>
          </router-link>
        </li>
      </ul>
    </transition>
  </li>
</template>

<style>
.drawer-container:is(:hover, :has(:focus-visible)) .menu-item-show-hover,
.drawer-container-fixed .menu-item-show-hover {
  opacity: 1;
  display: flex;
  transition: all ease 0.5s;
}

/*
 * Every icon sits on the avatar's axis, 28px from the edge (the middle of the collapsed rail),
 * and does not move when the drawer opens: only the labels fade in.
 * Sub-pages hang from their module icon (tree line at 27px, its curve stopping short of the highlight's bar)
 * and, collapsed, line up under it.
 */
.menu-item > :not(:first-child) {
  transition: opacity 0.15s ease-out 0.08s;
}

.sub-menu-item {
  margin-left: 27px;
  padding-left: 1.25rem;
  transition: margin-left 0.2s cubic-bezier(0.23, 1, 0.32, 1), padding-left 0.2s cubic-bezier(0.23, 1, 0.32, 1), border-color 0.2s;
}

/* The last one has no line (last:border-0), so it makes up for the line's 2px. */
.sub-menu-item:last-child {
  padding-left: calc(1.25rem + 2px);
}

.sub-menu-item::after,
.sub-menu-item-icon {
  transition: opacity 0.2s;
}

@media (min-width: 768px) {
  .drawer-container:not(:hover, :has(:focus-visible)) .menu-item > :not(:first-child) {
    opacity: 0;
    transition-duration: 0.1s;
    transition-delay: 0s;
  }

  .drawer-container:not(:hover, :has(:focus-visible)) .sub-menu-item {
    margin-left: 0;
    padding-left: 10px;
    border-color: transparent;
  }

  .drawer-container:not(:hover, :has(:focus-visible)) .sub-menu-item::after {
    opacity: 0;
  }

  .drawer-container:not(:hover, :has(:focus-visible)) .sub-menu-item:last-child {
    padding-left: 12px;
  }

  .drawer-container:not(:hover, :has(:focus-visible)) .menu-item:not(.router-link-exact-active) > .sub-menu-item-icon {
    opacity: 0.6;
  }
}

@media (prefers-reduced-motion: reduce) {
  .menu-item > *,
  .sub-menu-item,
  .sub-menu-item::after,
  .sub-menu-item-icon {
    transition: none;
  }
}

.drawer-container:is(:hover, :has(:focus-visible)) .sub-menu-item::after,
.drawer-container-fixed .sub-menu-item::after {
  content: '';
}

/* The count after the label says it once the drawer is open. */
.drawer-container:is(:hover, :has(:focus-visible)) .menu-item-badge-dot,
.drawer-container-fixed .menu-item-badge-dot {
  display: none;
}
</style>
